import {
  type AuctionLogEntry,
  type AuctionRound,
  type BidHiddenCubeAction,
  type DecideHiddenCubeAction,
  HIDDEN_AUCTION_TRIGGER_ACCEPTED_COUNT,
  type HiddenAuctionState,
  type MinusAuctionPlayer,
  type MinusAuctionState,
  type PassHiddenCubeBidAction,
  type RejectCubeAction,
  type TakeCubeAction,
  type TransferGarnetAction,
} from "../types";
import { calculateScoreEntries, insertAtRandomIndex } from "../utils";

export function applyRejectCube(
  state: MinusAuctionState,
  action: RejectCubeAction,
): MinusAuctionState {
  const auction = requireAuction(state);
  const nextPlayers = state.players.map((player) => {
    if (player.playerId !== action.playerId) {
      return player;
    }
    return action.resource === "CHIP"
      ? { ...player, chips: player.chips - 1 }
      : { ...player, garnets: player.garnets - 1 };
  });
  const nextAuction: AuctionRound = {
    ...auction,
    pileChips: auction.pileChips + (action.resource === "CHIP" ? 1 : 0),
    pileGarnets: auction.pileGarnets + (action.resource === "GARNET" ? 1 : 0),
    currentPlayerId: getNextPlayerId(state.playerOrder, action.playerId),
  };

  return {
    ...state,
    players: nextPlayers,
    auction: nextAuction,
    auctionLog: [
      ...state.auctionLog,
      {
        message:
          action.resource === "CHIP"
            ? "칩 1개로 낙찰 거부"
            : "가넷 1개로 낙찰 거부",
        playerId: action.playerId,
        cube: auction.currentCube,
      },
    ],
  };
}

export function applyTakeCube(
  state: MinusAuctionState,
  action: TakeCubeAction,
): MinusAuctionState {
  const auction = requireAuction(state);
  const nextAcceptedCubeCount = state.acceptedCubeCount + 1;
  const nextPlayers = state.players.map((player) => {
    if (player.playerId !== action.playerId) {
      return player;
    }
    return {
      ...player,
      chips: player.chips + auction.pileChips,
      garnets: player.garnets + auction.pileGarnets,
      cubes: [...player.cubes, auction.currentCube].sort(
        (left, right) => left - right,
      ),
    };
  });
  const takeLog: AuctionLogEntry = {
    message: `타일 ${auction.currentCube} 낙찰`,
    playerId: action.playerId,
    cube: auction.currentCube,
  };
  const baseState: MinusAuctionState = {
    ...state,
    players: nextPlayers,
    acceptedCubeCount: nextAcceptedCubeCount,
    auctionLog: [...state.auctionLog, takeLog],
  };

  if (
    nextAcceptedCubeCount === HIDDEN_AUCTION_TRIGGER_ACCEPTED_COUNT &&
    !state.hiddenAuctionResolved &&
    state.secondHiddenCube !== null
  ) {
    return startHiddenAuction(baseState, action.playerId);
  }

  return revealNextAuction(baseState, action.playerId);
}

export function applyTransferGarnet(
  state: MinusAuctionState,
  action: TransferGarnetAction,
): MinusAuctionState {
  return {
    ...state,
    players: state.players.map((player) => {
      if (player.playerId === action.fromPlayerId) {
        return { ...player, garnets: player.garnets - action.amount };
      }
      if (player.playerId === action.toPlayerId) {
        return { ...player, garnets: player.garnets + action.amount };
      }
      return player;
    }),
    transferLog: [
      ...state.transferLog,
      {
        fromPlayerId: action.fromPlayerId,
        toPlayerId: action.toPlayerId,
        amount: action.amount,
        phase: state.phase,
      },
    ],
    auctionLog: [
      ...state.auctionLog,
      {
        message: `가넷 ${action.amount}개 양도`,
        playerId: action.fromPlayerId,
      },
    ],
  };
}

export function applyBidHiddenCube(
  state: MinusAuctionState,
  action: BidHiddenCubeAction,
): MinusAuctionState {
  const hiddenAuction = requireHiddenAuction(state);
  const nextHiddenAuction: HiddenAuctionState = {
    ...hiddenAuction,
    currentBid: action.amount,
    highestBidderId: action.playerId,
    lastBidByPlayerId: {
      ...hiddenAuction.lastBidByPlayerId,
      [action.playerId]: action.amount,
    },
    lockedGarnetsByPlayerId: {
      ...hiddenAuction.lockedGarnetsByPlayerId,
      ...(hiddenAuction.highestBidderId
        ? { [hiddenAuction.highestBidderId]: 0 }
        : {}),
      [action.playerId]: action.amount,
    },
  };
  return advanceHiddenAuctionTurn({
    ...state,
    hiddenAuction: nextHiddenAuction,
    auctionLog: [
      ...state.auctionLog,
      {
        message: `히든 타일 ${action.amount}G 입찰`,
        playerId: action.playerId,
      },
    ],
  });
}

export function applyPassHiddenCubeBid(
  state: MinusAuctionState,
  action: PassHiddenCubeBidAction,
): MinusAuctionState {
  const hiddenAuction = requireHiddenAuction(state);
  const nextHiddenAuction: HiddenAuctionState = {
    ...hiddenAuction,
    passedPlayerIds: hiddenAuction.passedPlayerIds.includes(action.playerId)
      ? hiddenAuction.passedPlayerIds
      : [...hiddenAuction.passedPlayerIds, action.playerId],
  };
  return advanceHiddenAuctionTurn({
    ...state,
    hiddenAuction: nextHiddenAuction,
    auctionLog: [
      ...state.auctionLog,
      {
        message: "히든 타일 입찰 포기",
        playerId: action.playerId,
      },
    ],
  });
}

export function applyDecideHiddenCube(
  state: MinusAuctionState,
  action: DecideHiddenCubeAction,
): MinusAuctionState {
  const hiddenAuction = requireHiddenAuction(state);
  const includedDeck =
    action.decision === "INCLUDE"
      ? insertAtRandomIndex(
          state.deck,
          hiddenAuction.cube,
          state.seed + state.acceptedCubeCount + hiddenAuction.currentBid,
        )
      : state.deck;

  return revealNextAuction(
    {
      ...state,
      phase: "AUCTION_DECISION",
      deck: includedDeck,
      secondHiddenCube:
        action.decision === "INCLUDE" ? hiddenAuction.cube : null,
      secondHiddenCubeIncluded: action.decision === "INCLUDE",
      hiddenAuctionResolved: true,
      hiddenAuction: null,
      auctionLog: [
        ...state.auctionLog,
        {
          message:
            action.decision === "INCLUDE"
              ? "두 번째 히든 타일을 남은 경매에 포함"
              : "두 번째 히든 타일을 비공개 제외",
          playerId: action.playerId,
          cube: hiddenAuction.cube,
        },
      ],
    },
    hiddenAuction.resumePlayerId,
  );
}

export function getNextPlayerId(
  playerOrder: readonly string[],
  playerId: string,
): string {
  const index = playerOrder.indexOf(playerId);
  const next = playerOrder[(index + 1) % playerOrder.length];
  if (index < 0 || next === undefined) {
    throw new Error(`Unknown player: ${playerId}`);
  }
  return next;
}

export function getAvailableGarnets(
  state: MinusAuctionState,
  playerId: string,
): number {
  const player = getPlayer(state, playerId);
  const locked = state.hiddenAuction?.lockedGarnetsByPlayerId[playerId] ?? 0;
  return player.garnets - locked;
}

export function getPlayer(
  state: MinusAuctionState,
  playerId: string,
): MinusAuctionPlayer {
  const player = state.players.find(
    (candidate) => candidate.playerId === playerId,
  );
  if (!player) {
    throw new Error(`Unknown player: ${playerId}`);
  }
  return player;
}

function startHiddenAuction(
  state: MinusAuctionState,
  resumePlayerId: string,
): MinusAuctionState {
  const currentBidderId = state.playerOrder[0];
  if (state.secondHiddenCube === null || currentBidderId === undefined) {
    return revealNextAuction(state, resumePlayerId);
  }

  return {
    ...state,
    phase: "HIDDEN_AUCTION_BIDDING",
    auction: null,
    hiddenAuction: {
      cube: state.secondHiddenCube,
      currentBid: 0,
      currentBidderId,
      highestBidderId: null,
      passedPlayerIds: [],
      lastBidByPlayerId: {},
      lockedGarnetsByPlayerId: {},
      resumePlayerId,
    },
    auctionLog: [
      ...state.auctionLog,
      { message: "17번째 낙찰 완료 · 두 번째 히든 타일 경매 시작" },
    ],
  };
}

function advanceHiddenAuctionTurn(state: MinusAuctionState): MinusAuctionState {
  const hiddenAuction = requireHiddenAuction(state);
  const activeBidderIds = state.playerOrder.filter(
    (playerId) => !hiddenAuction.passedPlayerIds.includes(playerId),
  );

  if (hiddenAuction.highestBidderId === null && activeBidderIds.length === 0) {
    return revealNextAuction(
      {
        ...state,
        phase: "AUCTION_DECISION",
        secondHiddenCube: null,
        hiddenAuctionResolved: true,
        hiddenAuction: null,
        auctionLog: [
          ...state.auctionLog,
          { message: "아무도 입찰하지 않아 두 번째 히든 타일 비공개 제외" },
        ],
      },
      hiddenAuction.resumePlayerId,
    );
  }

  if (
    hiddenAuction.highestBidderId !== null &&
    activeBidderIds.every(
      (playerId) => playerId === hiddenAuction.highestBidderId,
    )
  ) {
    return {
      ...state,
      phase: "HIDDEN_AUCTION_DECISION",
      players: state.players.map((player) =>
        player.playerId === hiddenAuction.highestBidderId
          ? { ...player, garnets: player.garnets - hiddenAuction.currentBid }
          : player,
      ),
      hiddenAuction: {
        ...hiddenAuction,
        currentBidderId: null,
      },
      auctionLog: [
        ...state.auctionLog,
        {
          message: `두 번째 히든 타일 ${hiddenAuction.currentBid}G 낙찰`,
          playerId: hiddenAuction.highestBidderId,
        },
      ],
    };
  }

  const nextBidderId = findNextHiddenBidder(state.playerOrder, hiddenAuction);
  return {
    ...state,
    hiddenAuction: {
      ...hiddenAuction,
      currentBidderId: nextBidderId,
    },
  };
}

function findNextHiddenBidder(
  playerOrder: readonly string[],
  hiddenAuction: HiddenAuctionState,
): string | null {
  const startIndex =
    hiddenAuction.currentBidderId === null
      ? 0
      : playerOrder.indexOf(hiddenAuction.currentBidderId) + 1;

  for (let offset = 0; offset < playerOrder.length; offset += 1) {
    const playerId = playerOrder[(startIndex + offset) % playerOrder.length];
    if (
      playerId &&
      playerId !== hiddenAuction.highestBidderId &&
      !hiddenAuction.passedPlayerIds.includes(playerId)
    ) {
      return playerId;
    }
  }

  return null;
}

function revealNextAuction(
  state: MinusAuctionState,
  startPlayerId: string,
): MinusAuctionState {
  const [nextCube, ...remainingDeck] = state.deck;
  if (nextCube === undefined) {
    return finishGame({
      ...state,
      auction: null,
      phase: "GAME_OVER",
      status: "COMPLETED",
    });
  }

  return {
    ...state,
    phase: "AUCTION_DECISION",
    deck: remainingDeck,
    auction: {
      currentCube: nextCube,
      pileChips: 0,
      pileGarnets: 0,
      startPlayerId,
      currentPlayerId: startPlayerId,
    },
    auctionLog: [
      ...state.auctionLog,
      {
        message: `새 타일 ${nextCube} 공개`,
        playerId: startPlayerId,
        cube: nextCube,
      },
    ],
  };
}

function finishGame(state: MinusAuctionState): MinusAuctionState {
  const scores = calculateScoreEntries(state.players);
  const winningScore = scores[0] ?? null;
  const losingScore = scores.at(-1) ?? null;

  return {
    ...state,
    status: "COMPLETED",
    phase: "GAME_OVER",
    auction: null,
    hiddenAuction: null,
    scores,
    winnerIds: scores
      .filter((score) => scoreMatches(score, winningScore))
      .map((score) => score.playerId),
    eliminationCandidateIds: scores
      .filter((score) => scoreMatches(score, losingScore))
      .map((score) => score.playerId),
    auctionLog: [...state.auctionLog, { message: "모든 타일 경매 종료" }],
  };
}

function scoreMatches(
  score: { readonly finalScore: number; readonly chipSurplus: number },
  target: { readonly finalScore: number; readonly chipSurplus: number } | null,
): boolean {
  return (
    target !== null &&
    score.finalScore === target.finalScore &&
    score.chipSurplus === target.chipSurplus
  );
}

function requireAuction(state: MinusAuctionState): AuctionRound {
  if (!state.auction) {
    throw new Error("Auction round is not active");
  }
  return state.auction;
}

function requireHiddenAuction(state: MinusAuctionState): HiddenAuctionState {
  if (!state.hiddenAuction) {
    throw new Error("Hidden auction is not active");
  }
  return state.hiddenAuction;
}
